import { useEffect, useState } from "react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Switch } from "@/components/ui/switch"
import { CATEGORIAS } from "@/lib/consentimento"
import { useConsentimentoStore } from "@/stores/consentimentoStore"

/**
 * Modal de preferências de cookies. Estado local (rascunho) sincronizado ao
 * abrir; as categorias vêm de `CATEGORIAS` e iniciam todas marcadas.
 */
export function CookiePreferencesDialog() {
  const aberto = useConsentimentoStore((s) => s.modalAberto)
  const salvar = useConsentimentoStore((s) => s.salvar)
  const aceitarTodos = useConsentimentoStore((s) => s.aceitarTodos)
  const fechar = useConsentimentoStore((s) => s.fecharPreferencias)
  const preferenciasStored = useConsentimentoStore((s) => s.preferencias)
  const analiticosStored = useConsentimentoStore((s) => s.analiticos)

  const [preferencias, setPreferencias] = useState(preferenciasStored)
  const [analiticos, setAnaliticos] = useState(analiticosStored)

  useEffect(() => {
    if (aberto) {
      setPreferencias(preferenciasStored)
      setAnaliticos(analiticosStored)
    }
  }, [aberto, preferenciasStored, analiticosStored])

  const valorDe = (id: string) =>
    id === "preferencias" ? preferencias : id === "analiticos" ? analiticos : true
  const alternar = (id: string, valor: boolean) => {
    if (id === "preferencias") setPreferencias(valor)
    else if (id === "analiticos") setAnaliticos(valor)
  }

  return (
    <Dialog open={aberto} onOpenChange={(open) => !open && fechar()}>
      <DialogContent className="max-w-xl gap-0 p-0">
        <DialogHeader className="border-b px-6 py-5">
          <DialogTitle>Preferências de cookies</DialogTitle>
          <DialogDescription>
            Escolha quais categorias de cookies o SIGI pode usar. As categorias essenciais ficam
            sempre ativas. Detalhes na{" "}
            <a
              href="/privacidade"
              target="_blank"
              rel="noreferrer"
              className="font-medium text-primary underline underline-offset-2"
            >
              Política de Privacidade
            </a>{" "}
            e nos{" "}
            <a
              href="/termos"
              target="_blank"
              rel="noreferrer"
              className="font-medium text-primary underline underline-offset-2"
            >
              Termos de Uso
            </a>
            .
          </DialogDescription>
        </DialogHeader>

        <div className="divide-y px-6">
          {CATEGORIAS.map((categoria) => (
            <div key={categoria.id} className="flex items-start gap-4 py-4">
              <div className="flex-1 space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">{categoria.titulo}</span>
                  {categoria.obrigatoria && (
                    <Badge variant="secondary" className="font-normal">
                      Sempre ativo
                    </Badge>
                  )}
                </div>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  {categoria.descricao}
                </p>
              </div>
              <Switch
                checked={valorDe(categoria.id)}
                disabled={categoria.obrigatoria}
                onCheckedChange={(valor) => alternar(categoria.id, valor)}
                aria-label={categoria.titulo}
              />
            </div>
          ))}
        </div>

        <DialogFooter className="border-t bg-muted/40 px-6 py-4 sm:justify-between">
          <Button variant="ghost" size="sm" onClick={aceitarTodos}>
            Aceitar todos
          </Button>
          <Button size="sm" onClick={() => salvar({ preferencias, analiticos })}>
            Salvar preferências
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
